/**
 * Staff calls · Ringtone & Do not disturb.
 *
 * The ringtone: seven tones made in the browser (ringtone.ts), each with a
 * Preview, and a volume. The ring card uses the chosen one; the meeting
 * reminder uses the chime, quieter. "Mute the ringtone" stays where it was,
 * in the bar above the directory and on the ring card.
 *
 * Do not disturb: on for an hour, until seven tomorrow, or until turned off —
 * the header's phone menu does the same in one click. While it is on, calls
 * do not ring here or on the phone app: the caller is told, and the call is a
 * missed one in your list. The people on the allow-list still ring through;
 * so does an urgent call from a level the administrator lets break through.
 */
import { useState } from "react";
import { useCallDirectory } from "../../api/queries";
import { Button, Card } from "../../design/components";
import { DND_CHOICE_LABEL, type DndChoice } from "../../domain/calls";
import { t } from "../../i18n";
import type { CallsApi } from "./CallsProvider";
import { dndUntilText } from "./CallsMenu";
import { playOnce, TONES, type ToneId } from "./ringtone";
import s from "./Calls.module.css";

const CHOICES: DndChoice[] = ["hour", "tomorrow", "forever"];

export function RingtoneDndCard({ calls }: { calls: CallsApi }) {
  const { ring, setRing, dnd, dndOn } = calls;
  const dir = useCallDirectory();
  const people = dir.data?.items ?? [];
  const [adding, setAdding] = useState("");
  const allow = dnd.allow.map((a) => a.principalId);
  const nameOf = (id: string) =>
    dnd.allow.find((a) => a.principalId === id)?.name || people.find((p) => p.principalId === id)?.name || id;
  /* the allow-list is kept whether or not Do not disturb is on; changing it keeps the time */
  const saveAllow = (next: string[]) => {
    const until = !dndOn ? null : dnd.forever ? "forever" : (dnd.until ?? null);
    void calls.setDndUntil(until, next);
  };
  const choose = (tone: ToneId) => {
    setRing({ ...ring, tone });
    playOnce(tone, ring.volume);
  };

  return (
    <div id="ringtone">
      <Card
        title="Ringtone & Do not disturb"
        sub="Ring sound on this device, and quiet times"
        testId="ringtone-card"
      >
        <div className={s.rtGrid}>
          <section className={s.rtCol}>
            <h3 className={s.rtHead}>{t("Ringtone")}</h3>
            <div className={s.tones} role="radiogroup" aria-label={t("Ringtone")}>
              {TONES.map((x) => (
                <div key={x.id} className={s.tone} data-on={ring.tone === x.id} data-testid={`tone-${x.id}`}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={ring.tone === x.id}
                    className={s.toneMain}
                    onClick={() => choose(x.id)}
                  >
                    <span className={s.toneDot} aria-hidden="true" />
                    <span>
                      <b>{t(x.label)}</b>
                      <span className={s.dim}>{t(x.hint)}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={s.preview}
                    onClick={() => playOnce(x.id, ring.volume)}
                    aria-label={t("Preview {name}", { name: t(x.label) })}
                    title={t("Preview")}
                    data-testid={`preview-${x.id}`}
                  >
                    ▶
                  </button>
                </div>
              ))}
            </div>
            <label className={s.volume}>
              <span>{t("Volume")}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={ring.volume}
                onChange={(e) => setRing({ ...ring, volume: Number(e.target.value) })}
                onPointerUp={() => playOnce(ring.tone, ring.volume)}
                onKeyUp={() => playOnce(ring.tone, ring.volume)}
                data-testid="ring-volume"
              />
              <b className={s.volumeNum}>{ring.volume}%</b>
            </label>
            <p className={s.note}>
              {t(
                "Saved in this browser. Meeting reminders play a quieter chime. The first ring may be silent until you click the page once.",
              )}
            </p>
          </section>

          <section className={s.rtCol} data-testid="dnd-panel">
            <h3 className={s.rtHead}>{t("Do not disturb")}</h3>
            <div className={s.dndState} data-on={dndOn} data-testid="dnd-state">
              <span className={s.dndMoon} aria-hidden="true">
                ☾
              </span>
              <span>
                <b>{dndOn ? t("On") : t("Off")}</b>{" "}
                {dndOn
                  ? t("Calls do not ring, {until}. Callers see a missed call.", {
                      until: dndUntilText(dnd.until, dnd.forever),
                    })
                  : t("Calls ring as usual.")}
              </span>
            </div>
            <div className={s.dndChoices}>
              <Button
                size="sm"
                variant={!dndOn ? "primary" : "default"}
                disabled={calls.dndBusy}
                onClick={() => void calls.setDnd(null)}
                data-testid="dnd-card-off"
              >
                {t("Off")}
              </Button>
              {CHOICES.map((c) => (
                <Button
                  key={c}
                  size="sm"
                  variant={dndOn && c === "forever" && dnd.forever ? "primary" : "default"}
                  disabled={calls.dndBusy}
                  onClick={() => void calls.setDnd(c)}
                  data-testid={`dnd-card-${c}`}
                >
                  {t(DND_CHOICE_LABEL[c])}
                </Button>
              ))}
            </div>
            <div className={s.allowList}>
              <b>{t("Still rings through:")}</b>
              {allow.length === 0 && <span className={s.dim}>{t("nobody")}</span>}
              {allow.map((id) => (
                <span key={id} className={s.chip}>
                  {nameOf(id)}
                  <button
                    type="button"
                    aria-label={t("Remove {name}", { name: nameOf(id) })}
                    disabled={calls.dndBusy}
                    onClick={() => saveAllow(allow.filter((x) => x !== id))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className={s.formRow}>
              <select
                className={s.select}
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
                aria-label={t("Choose a person who may ring through")}
                data-testid="dnd-allow-pick"
              >
                <option value="">{t("Add a person…")}</option>
                {people
                  .filter((p) => !allow.includes(p.principalId))
                  .map((p) => (
                    <option key={p.principalId} value={p.principalId}>
                      {p.name}
                    </option>
                  ))}
              </select>
              <Button
                size="sm"
                disabled={!adding || calls.dndBusy}
                onClick={() => {
                  saveAllow([...allow, adding]);
                  setAdding("");
                }}
                data-testid="dnd-allow-add"
              >
                {t("Add")}
              </Button>
            </div>
            {calls.dndError && (
              <p className={s.refused} role="alert">
                {calls.dndError}
              </p>
            )}
            <p className={s.note}>
              {t(
                "People on this list always ring through Do not disturb. Urgent calls also ring through if the administrator allows the caller's level (Who can call whom).",
              )}
            </p>
          </section>
        </div>
      </Card>
    </div>
  );
}
